import { FC } from 'react';

import Statistics from 'components/Statistics';

import SlideOver from 'primitives/SlideOver';

import constants from 'utils/constants';
import {
  fixToTwoLocalPrice,
  formatCurrencyToShort,
  getInvestedAmountRange,
} from 'utils/format-helper';

import InvestmentStatistics from '../../Users/SyndicateUserSlideOver/InvestmentStatistics';
import SyndicateUserTitle from '../../Users/SyndicateUserSlideOver/SyndicateUserTitle';

export interface CoSyndicateApplicableUser {
  user: {
    id: string;
    name: string;
    image: {
      url: string;
      name: string;
    };
    email: string;
    linkedInProfileUrl: string;
    createdAt: string;
    statistics: {
      numberOfSyndicatesJoined: number;
      totalInvestedAmount: number;
      numberOfInvestments: number;
      investRate: number;
    };
  };
  syndicateUsers: SyndicateUser[];
}

interface SyndicateUser {
  id: string;
  createdAt: string;
  syndicate: {
    id: string;
    name: string;
    image: string;
  };
  statistics: {
    totalInvestedAmount: number;
    numberOfInvestments: number;
    investRate: number;
  };
}

const CoSyndicateApplicableUserSlideOver: FC<{
  open: boolean;
  coSyndicateApplicableUser?: CoSyndicateApplicableUser | null;
  onClose: () => void;
}> = ({ open = false, coSyndicateApplicableUser, onClose }) => {
  function renderContent() {
    if (!coSyndicateApplicableUser) return null;

    const syndicateUsers = coSyndicateApplicableUser.syndicateUsers;
    const user = coSyndicateApplicableUser.user;

    return (
      <div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img
              className="h-12 w-12 rounded-full"
              src={user.image?.url ?? constants.ALT_AVATAR_URL}
              alt=""
            />
            <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
          </div>
        </div>
        <div className="my-5">
          <Statistics
            statistics={[
              { label: 'Investor Since', value: user.createdAt, type: 'MONTH' },
              {
                label: 'Number of Syndicates Joined',
                value: user.statistics.numberOfSyndicatesJoined,
                type: 'NUMBER',
              },
            ]}
          />
          <div className="p-4 bg-gray-400/5 rounded-xl">
            <p className="text-md font-medium text-gray-700">Last 12 mo. on AngelList India</p>
            <p className="text-xs text-gray-400">Via SPVs and Funds</p>
            <InvestmentStatistics
              data={[
                {
                  label: 'Total invested',
                  value: getInvestedAmountRange(user.statistics.totalInvestedAmount),
                },
                {
                  label: 'Investments',
                  value: user.statistics.numberOfInvestments,
                },
                {
                  label: 'Invest Rate',
                  value: `${fixToTwoLocalPrice(user.statistics.investRate)}%` || 'No history',
                },
              ]}
            />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          {syndicateUsers.map(syndicateUser => (
            <div className="p-4 bg-gray-400/5 rounded-xl" key={syndicateUser.id}>
              <SyndicateUserTitle
                name={syndicateUser.syndicate.name}
                image={syndicateUser.syndicate.image}
                createdAt={syndicateUser.createdAt}
                linkedInProfileUrl={user.linkedInProfileUrl}
                syndicateId={syndicateUser.syndicate.id}
                userId={user.id}
                onClose={onClose}
              />
              <InvestmentStatistics
                data={[
                  {
                    label: 'Total invested',
                    value: formatCurrencyToShort(syndicateUser.statistics.totalInvestedAmount),
                  },
                  {
                    label: 'Investments',
                    value: syndicateUser.statistics.numberOfInvestments,
                  },
                  {
                    label: 'Invest Rate',
                    value:
                      `${fixToTwoLocalPrice(syndicateUser.statistics.investRate)}%` || 'No history',
                  },
                ]}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <SlideOver open={open} onClose={onClose}>
      {renderContent()}
    </SlideOver>
  );
};

export default CoSyndicateApplicableUserSlideOver;
